import { useEffect, useMemo, useRef } from 'react';
import _ from 'lodash';
import { faCommentsDollar } from '@fortawesome/pro-regular-svg-icons';
import { classNames } from '@progress/kendo-react-common';
import {
    AdjustmentTypeFilter,
    AssigneeFilter,
    DateTabs,
    DisputeCard,
    DisputeReasonFilter,
    DisputeSortFilter,
    DisputeStatusFilter,
    ExportButton,
    Icon,
    LoaderOverlay,
    MarDisputeTypeFilter,
    PaginatedList,
    QueueFilter,
    SearchInput,
    SuspenseBoundary,
} from 'components';
import { AdjustmentTypeText, DisputeStatusText, DisputeTypeText } from 'enums';
import {
    useAccountParam,
    useDisputeFilterDefaults,
    useScrollPosition,
    useVisibility,
} from 'hooks';
import {
    useDisputeFilter,
    useDisputeReasons,
    useFilteredDisputes,
    useMarDisputeTypes,
    useRefreshFilteredDisputes,
    useUserInfo,
} from 'state';
import styles from './DisputesPage.module.scss';

export default function DisputesPage() {
    const accountId = useAccountParam();
    const {
        dateRange,
        setDateRange,
        setStatus,
        status,
        selectedAssignee,
        setAssignee,
        selectedQueue,
        setQueue,
        selectedAdjusmentType,
        setAdjustmentType,
        selectedMarDisputeTypeId,
        setMarDisputeTypeId,
        selectedReasonId,
        setReasonId,
        searchTerm,
        setSearchTerm,
        sort,
        setSort,
    } = useDisputeFilter();

    const refresh = useRefreshFilteredDisputes(accountId);
    const user = useUserInfo();
    const headerRef = useRef<HTMLDivElement | null>(null);
    const shouldShowCombinedHeader = useVisibility(headerRef);

    useDisputeFilterDefaults();

    const Filters = useMemo(
        () => (
            <div className={styles.filters}>
                <MarDisputeTypeFilter
                    marDisputeTypeId={selectedMarDisputeTypeId}
                    onMarDisputeTypeIdChange={setMarDisputeTypeId}
                />
                <DisputeReasonFilter
                    disputeReasonId={selectedReasonId}
                    onDisputeReasonIdChange={setReasonId}
                />
                <SearchInput
                    placeholder="Filter"
                    value={searchTerm ?? ""}
                    onValueChange={(value) => setSearchTerm(value || undefined)}
                />
                <DisputeStatusFilter onStatusChange={setStatus} status={status} />
                <AdjustmentTypeFilter
                    onAdjusmentTypeChange={setAdjustmentType}
                    adjustmentType={selectedAdjusmentType}
                />
                {user?.isInternalUser && (
                    <>
                        <QueueFilter
                            queue={selectedQueue}
                            onQueueChange={setQueue}
                            defaultQueueId={user.disputeQueueId}
                        />
                        <AssigneeFilter
                            assignee={selectedAssignee}
                            onAssigneeChange={setAssignee}
                        />
                    </>
                )}
                <DisputeSortFilter onDisputeSortChange={setSort} disputeSort={sort} />
            </div>
        ),
        [
            searchTerm,
            setSearchTerm,
            selectedAdjusmentType,
            setAdjustmentType,
            selectedAssignee,
            setAssignee,
            selectedMarDisputeTypeId,
            setMarDisputeTypeId,
            selectedReasonId,
            setReasonId,
            sort,
            setSort,
            status,
            setStatus,
            selectedQueue,
            setQueue,
            user.disputeQueueId,
            user?.isInternalUser,
        ]
    );

    const Headers = useMemo(
        () => (
            <div className={styles.pageHeader}>
                {shouldShowCombinedHeader ? (
                    <div className={styles.title}>
                        <span>
                            <Icon icon={faCommentsDollar} />
                        </span>
                        <span>Disputes</span>
                    </div>
                ) : (
                    <></>
                )}

                <DateTabs
                    dateRange={dateRange}
                    setDateRange={setDateRange}
                />
            </div>
        ),
        [dateRange, setDateRange, shouldShowCombinedHeader]
    );

    const FiltersAndHeader = useMemo(
        () => (
            <div
                className={classNames(styles.fixedHeaderAndFilter, {
                    [styles.hidden]: shouldShowCombinedHeader,
                })}
            >
                {Filters}
                {Headers}
            </div>
        ),
        [Filters, Headers, shouldShowCombinedHeader]
    );

    return (
        <div className={styles.container}>
            <div
                className={classNames({ [styles.hidden]: !shouldShowCombinedHeader })}
                ref={headerRef}
            >
                {Headers}
            </div>
            {FiltersAndHeader}
            <div className={styles.content}>
                <div
                    className={classNames(styles.filters, {
                        [styles.hidden]: !shouldShowCombinedHeader,
                    })}
                >
                    {Filters}
                </div>
                <SuspenseBoundary
                    errorMessage={`Error retrieving disputes for account ${accountId}`}
                    loadingComponent={<DisputesLoadingComponent />}
                    onReset={refresh}
                >
                    <DisputesComponent />
                </SuspenseBoundary>
            </div>
        </div>
    );
}

const DisputesLoadingComponent = () => (
    <LoaderOverlay
        isLoading
        container
        height="18rem"
        width="100%"
    />
);

const DisputesComponent = () => {
    const accountId = useAccountParam();
    const { restoreScrollPosition } = useScrollPosition();
    const disputes = useFilteredDisputes(accountId);
    const isImpersonating = !useUserInfo()?.isInternalUser;

    const baseFieldsToExclude = [
        "updatedBy",
        "invoiceDetailLines",
        "paymentDetailLines",
        "disputeId",
    ];
    const impersonationExtraFields = [
        "disputeQueueId",
        "resolutionReasonId",
        "approvedAmount",
        "assigneeEmail",
        "assigneeFirstName",
        "assigneeLastName",
    ];
    const fieldsToExclude = isImpersonating
        ? [...baseFieldsToExclude, ...impersonationExtraFields]
        : baseFieldsToExclude;

    const disputeReasons = useDisputeReasons();
    const marDisputeTypes = useMarDisputeTypes();
    const mappedDisputes = disputes.map((d) => ({
        ...d,
        disputeType: DisputeTypeText[d.disputeType] ?? d.disputeType,
        disputeStatusId: DisputeStatusText[d.disputeStatusId] ?? d.disputeStatusId,
        adjustmentTypeId:
            AdjustmentTypeText[d.adjustmentTypeId] ?? d.adjustmentTypeId,
        disputeReasonId:
            disputeReasons.find((r) => r.disputeReasonId === d.disputeReasonId)
                ?.description ?? d.disputeReasonId,
        marDisputeTypeId:
            marDisputeTypes.find((m) => m.marDisputeTypeId === d.marDisputeTypeId)
                ?.description ?? d.marDisputeTypeId,
    }));

    useEffect(() => {
        restoreScrollPosition();
    }, [disputes, restoreScrollPosition]);

    return !_.isEmpty(disputes) ? (
        <>
            <div className="details-page-main-header-export">
                <ExportButton
                    data={mappedDisputes}
                    fieldsToExclude={fieldsToExclude}
                    fileName={`Disputes-${accountId || "nonaccount"}`}
                    removeIdSuffix={true}
                />
            </div>
            <PaginatedList
                items={disputes}
                componentKey="Disputes"
                resetKey={accountId}
            >
                {(dispute) => (
                    <DisputeCard key={dispute.disputeNumber} disputeSummary={dispute} />
                )}
            </PaginatedList>
        </>
    ) : (
        <div className={styles.notfound}>
            No disputes found for the selected date range and filters.
        </div>
    );
};
